import { ArrowRight, Search } from "lucide-react";
import { useEffect, useRef, type RefObject } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { preloadTurnstileScript, TurnstileWidget, type TurnstileWidgetHandle } from "@/components/TurnstileWidget";

interface SearchFormProps {
  query: string;
  maxReposInput: string;
  isBusy: boolean;
  formError: string | null;
  configError: string | null;
  turnstileSiteKey: string | null;
  turnstileRef: RefObject<TurnstileWidgetHandle | null>;
  showTurnstile: boolean;
  onQueryChange: (query: string) => void;
  onMaxReposChange: (value: string) => void;
  onSubmit: () => void;
  onTurnstileTokenChange: (token: string | null) => void;
  onTurnstileError: (message: string) => void;
}

export function SearchForm({
  query,
  maxReposInput: _maxReposInput,
  isBusy,
  formError,
  configError,
  turnstileSiteKey,
  turnstileRef,
  showTurnstile,
  onQueryChange,
  onMaxReposChange: _onMaxReposChange,
  onSubmit,
  onTurnstileTokenChange,
  onTurnstileError,
}: SearchFormProps) {
  const onTurnstileErrorRef = useRef(onTurnstileError);

  useEffect(() => {
    onTurnstileErrorRef.current = onTurnstileError;
  }, [onTurnstileError]);

  useEffect(() => {
    if (!turnstileSiteKey) {
      return;
    }

    void preloadTurnstileScript().catch(() => {
      onTurnstileErrorRef.current("Failed to load Turnstile.");
    });
  }, [turnstileSiteKey]);

  return (
    <form
      className="space-y-4"
      onSubmit={event => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <label htmlFor="query" className="sr-only">
        Search query
      </label>
      <div className="mx-auto w-full max-w-5xl">
        <div className="flex items-center gap-2 rounded-[32px] border border-slate-300/80 bg-white/96 pl-4 pr-4 py-3 shadow-[0_20px_45px_-34px_rgba(15,23,42,0.35)] transition-[border-color,box-shadow] focus-within:border-slate-400 focus-within:shadow-[0_24px_50px_-34px_rgba(15,23,42,0.4)]">
          <div className="flex size-9 shrink-0 items-center justify-center">
            <Search className="size-5 text-slate-400" />
          </div>
          <Input
            id="query"
            autoComplete="off"
            placeholder="Rust async runtime contributors"
            value={query}
            onChange={event => onQueryChange(event.target.value)}
            className="h-12 flex-1 border-0 bg-transparent px-0 text-base text-foreground shadow-none placeholder:text-slate-400 focus-visible:ring-0 md:text-lg"
          />
          <Button
            type="submit"
            size="icon-lg"
            disabled={isBusy}
            aria-label={isBusy ? "Searching" : "Search"}
            className="size-11 rounded-full border border-primary bg-primary text-primary-foreground shadow-none"
          >
            <ArrowRight className="size-5" />
          </Button>
        </div>
      </div>

      {showTurnstile ? (
        <div className="mx-auto w-full max-w-3xl">
          <div className="draft-box bg-background/45 px-4 py-4">
            {turnstileSiteKey ? (
              <TurnstileWidget
                ref={turnstileRef}
                siteKey={turnstileSiteKey}
                onTokenChange={onTurnstileTokenChange}
                onError={onTurnstileError}
              />
            ) : (
              <p className="text-sm text-muted-foreground">{configError ?? "Loading verification…"}</p>
            )}
          </div>
        </div>
      ) : null}

      {formError ? <p className="mx-auto w-full max-w-3xl px-2 text-left text-sm text-destructive">{formError}</p> : null}
    </form>
  );
}
